import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isApiError, getErrorMessage } from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Checkbox from '@/components/ui/Checkbox';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const REMEMBER_EMAIL_KEY = 'kampus-remember-email';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '', general: '' });

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false,
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Load saved email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);
    if (savedEmail) {
      setFormData({
        email: savedEmail,
        password: '',
        remember: true,
      });
    }
  }, []);

  const handleRememberChange = (checked: boolean) => {
    setFormData({ ...formData, remember: checked });

    if (!checked) {
      // Clear saved email if unchecking
      localStorage.removeItem(REMEMBER_EMAIL_KEY);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFieldErrors({ email: '', password: '', general: '' });

    // Validate inputs
    if (!formData.email || !formData.password) {
      setFieldErrors({
        email: formData.email ? '' : 'Email is required',
        password: formData.password ? '' : 'Password is required',
        general: '',
      });
      setIsLoading(false);
      return;
    }

    try {
      // Use the auth hook to login
      await login(formData.email, formData.password);

      // Save email if "Remember me" is checked
      if (formData.remember) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, formData.email);
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }

      // Navigate to dashboard (useEffect will handle redirect)
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      
      if (isApiError(err)) {
        // Handle specific API errors
        if (err.isAuthError()) {
          setFieldErrors({
            email: '',
            password: 'Invalid email or password',
            general: '',
          });
        } else if (err.isValidationError()) {
          setFieldErrors({
            email: '',
            password: '',
            general: err.message || 'Please check your input',
          });
        } else if (err.isServerError()) {
          setFieldErrors({
            email: '',
            password: '',
            general: 'Server error. Please try again later.',
          });
        } else {
          setFieldErrors({
            email: '',
            password: '',
            general: err.message,
          });
        }
      } else {
        // Handle network or unexpected errors
        const errorMessage = getErrorMessage(err);
        setFieldErrors({
          email: '',
          password: '',
          general: errorMessage,
        });
      }
    }

    setIsLoading(false);
  };

  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="px-8 pt-8 pb-6 text-center">
        <img 
          src="/logo.png" 
          alt="Kampus Logo" 
          className="w-32 h-32 mx-auto mb-4 object-contain"
        />
        <h1 className="text-2xl font-bold text-slate-800">Welcome back</h1>
        <p className="text-slate-500 mt-2">Sign in to your Kampus account</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="px-8 pb-8">
        <div className="space-y-5">
          <Input
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={fieldErrors.email}
            leftIcon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            }
          />

          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={fieldErrors.password}
              leftIcon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              }
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Checkbox
              label="Remember me"
              checked={formData.remember}
              onChange={(e) => handleRememberChange(e.target.checked)}
            />
            <a href="#" className="text-sm font-medium text-primary-500 hover:text-primary-600">
              Forgot password?
            </a>
          </div>

          {/* General error message */}
          {fieldErrors.general && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{fieldErrors.general}</p>
            </div>
          )}

          <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
            Sign in
          </Button>
        </div>
      </form>
    </div>
  );
}
